import { useEffect, useMemo, useState } from 'react';
import {
  avisoMesesCerrados,
  avisoNoEsGasto,
  basesDeReparto,
  gastosQueCuentan,
  isMaterialCategory,
  visibleCategory,
  debePreguntarReparto,
  etiquetaReparto,
  mesesDelReparto,
  pagoQueNoEsGasto,
  proponerReparto,
  repartoRecordado,
  repartoSoloDeEsteMes,
  resumenReparto,
  textoRecordado,
  MAX_MESES_REPARTO,
  addMonths,
  monthOf,
  periodOfMonth,
  type ExpenseEntry,
  type GastoParaRepartir,
  type NoEsGasto,
  type RepartoGasto,
} from '@alexiarest/shared';
import { useExpenses, useRevenues } from '../api/hooks';
import { formatMoney, formatMonthLong, today } from '../format';
import './reparto.css';

/**
 * PAGOS QUE CUBREN VARIOS MESES (bloque R, decidido con Alex el 11 oct 2026).
 *
 * Dos piezas pequeñas para las pantallas de otros bloques:
 *  - `EtiquetaReparto`: en la lista de Gastos, junto al pago entero,
 *    «Repartido en 12 meses · $750 al mes».
 *  - `PreguntaReparto`: dentro del panel del gasto (bloque G) y del escáner
 *    (bloque S). Sale solo cuando toca, con la respuesta ya propuesta, y
 *    recuerda lo de la vez anterior del mismo proveedor y categoría. Si no se
 *    contesta, el gasto cuenta entero en su mes (no bloquea nada).
 *
 * Guardado: el valor va en el gasto como `reparto` y `noEsGasto`
 * (`ExpenseInput`). `null` = quitarlo; ausente = la API conserva lo que había.
 */

export function EtiquetaReparto({ gasto }: { gasto: Pick<ExpenseEntry, 'date' | 'netAmount' | 'reparto'> }): JSX.Element | null {
  const texto = etiquetaReparto(gasto, formatMoney);
  if (!texto) return null;
  return <span className="etiqueta-reparto" title="Lo que salió de tu dinero es el pago entero; en el Estado de resultados cada mes carga solo su parte.">{texto}</span>;
}

const FECHA_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Al cambiar la fecha del gasto, el reparto se mueve con ella: «solo de este
 * mes» pasa al mes nuevo y uno de varios meses se desplaza lo mismo que la
 * fecha (un seguro de septiembre a agosto que se pasa al 20/10 cubre de
 * octubre a septiembre). Sin reparto, o sin fechas válidas, no cambia nada.
 */
export function repartoConFecha<T extends RepartoGasto | null | undefined>(reparto: T, antes: string, ahora: string): T {
  if (!reparto || !FECHA_RE.test(antes) || !FECHA_RE.test(ahora)) return reparto;
  const mesAntes = monthOf(antes);
  const mesAhora = monthOf(ahora);
  if (mesAntes === mesAhora) return reparto;
  if (reparto.meses <= 1) return { ...reparto, desde: mesAhora } as T;
  const [ya, ma] = mesAntes.split('-').map(Number);
  const [yb, mb] = mesAhora.split('-').map(Number);
  return { ...reparto, desde: addMonths(reparto.desde, (yb - ya) * 12 + (mb - ma)) } as T;
}

export interface ValorReparto {
  reparto?: RepartoGasto | null;
  noEsGasto?: NoEsGasto | null;
}

interface PreguntaRepartoProps {
  /** El gasto tal como está en el formulario (sin IVA). */
  gasto: GastoParaRepartir;
  /** Nombre del proveedor, para «La próxima factura de Seguros del Norte…». */
  proveedor?: string;
  /** Gastos anteriores (para «lo normal de ese proveedor» y para recordar). */
  historial: ExpenseEntry[];
  value: ValorReparto;
  onChange: (value: ValorReparto) => void;
  /** País de la cuenta: «a Hacienda» o «al SAT». */
  pais?: string;
  /**
   * Gasto nuevo (alta o escáner). Solo entonces se aplica automáticamente «no es gasto». Lo recordado
   * se propone, sin aplicarlo hasta que la persona confirme.
   */
  nuevo?: boolean;
}

function useBasesReparto(reparto: RepartoGasto | null | undefined, mesPago: string, historial: ExpenseEntry[]) {
  const meses = reparto ? mesesDelReparto(reparto) : [];
  const periodo = useMemo(() => ({
    start: periodOfMonth(meses[0] ?? mesPago).start,
    end: periodOfMonth(meses[meses.length - 1] ?? mesPago).end,
  }), [meses[0], meses[meses.length - 1], mesPago]);
  const ventas = useRevenues(periodo, reparto?.como === 'ventas');
  const sueldos = useExpenses(periodo, reparto?.como === 'sueldos');
  return basesDeReparto(ventas.data ?? [], gastosQueCuentan(sueldos.data ?? historial));
}

const decision = (reparto: RepartoGasto, hoy: string): RepartoGasto => ({ ...reparto, decididoEl: hoy, decididoEn: new Date().toISOString() });

export function PreguntaReparto({ gasto, proveedor, historial, value, onChange, pais, nuevo = true }: PreguntaRepartoProps): JSX.Element | null {
  const hoy = today();
  const mesPago = monthOf(gasto.date || hoy);
  const noEsGasto = pagoQueNoEsGasto(gasto.concept);
  const recordado = useMemo(() => (gasto.date ? repartoRecordado(historial, gasto) : undefined), [historial, gasto]);
  const [editando, setEditando] = useState(false);

  // Regla 8: el IVA a Hacienda no es gasto. Se marca solo; «Sí es un gasto» lo quita.
  useEffect(() => {
    if (nuevo && noEsGasto && value.noEsGasto === undefined) onChange({ ...value, noEsGasto });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuevo, noEsGasto, value.noEsGasto]);

  // Lo recordado es una propuesta: sin confirmar, el gasto sigue entero en su mes.
  const bases = useBasesReparto(value.reparto, mesPago, historial);

  if (value.noEsGasto) {
    return (
      <div className="reparto reparto--aviso" role="status">
        <p>{avisoNoEsGasto(value.noEsGasto, pais)}</p>
        <button type="button" className="reparto__enlace" onClick={() => onChange({ ...value, noEsGasto: null })}>Sí es un gasto</button>
      </div>
    );
  }

  if (!gasto.date || !(gasto.netAmount > 0)) return null;

  // Un gasto que ya existía, sin decidir: se propone, sin guardar nada hasta que se toque.
  if (!nuevo && noEsGasto && value.noEsGasto === undefined && value.reparto === undefined) {
    return (
      <div className="reparto reparto--pregunta">
        <p>{avisoNoEsGasto(noEsGasto, pais)}</p>
        <div className="reparto__botones">
          <button type="button" className="btn btn--sm" onClick={() => onChange({ ...value, noEsGasto })}>No es un gasto</button>
        </div>
      </div>
    );
  }
  const r = value.reparto;
  if (r) {
    const soloEsteMes = r.meses <= 1 && r.desde === mesPago;
    if (soloEsteMes && !editando) {
      return (
        <div className="reparto reparto--hecho">
          <p>
            {r.recordado ? textoRecordado(r, gasto.date) : `Listo: cuenta entero en ${formatMonthLong(mesPago).split(' ')[0]}.`}{' '}
            <button type="button" className="reparto__enlace" onClick={() => onChange({ ...value, reparto: null })}>Cambiar</button>
          </p>
        </div>
      );
    }
    const aviso = avisoMesesCerrados(r, hoy);
    return (
      <div className="reparto reparto--hecho">
        {!editando && <p className="reparto__resumen">
          <span aria-hidden="true">✓</span> {r.meses} meses · {resumenReparto(gasto.netAmount, r, formatMoney, bases)}
        </p>}
        {!editando && r.recordado && <p className="reparto__gris">{textoRecordado(r, gasto.date)}</p>}
        {!editando && aviso && <p className="reparto__cerrados">{aviso}</p>}
        {editando && <EditarReparto reparto={r} total={gasto.netAmount} historial={historial} hoy={hoy} onCancel={() => setEditando(false)} onChange={(nuevo) => { onChange({ ...value, reparto: decision({ ...nuevo, recordado: undefined }, hoy) }); setEditando(false); }} />}
        {!editando && <div className="reparto__acciones">
          <button type="button" className="reparto__enlace" onClick={() => setEditando(!editando)}>{editando ? 'Listo' : 'Cambiar cómo se reparte'}</button>
          <button type="button" className="reparto__enlace reparto__enlace--gris" onClick={() => { setEditando(false); onChange({ ...value, reparto: decision(repartoSoloDeEsteMes(gasto.date, hoy), hoy) }); }}>
            No repartir
          </button>
        </div>}
        {!r.recordado && (
          <p className="reparto__gris">
            {proveedor
              ? `La próxima factura de ${proveedor} tendrá esta propuesta (lo puedes cambiar).`
              : 'La próxima vez te propondré este reparto (lo puedes cambiar).'}
          </p>
        )}
      </div>
    );
  }

  // Sin respuesta: solo se pregunta cuando toca (regla 1). `null` = la
  // persona pidió «Cambiar»: se vuelve a preguntar aunque no tocara.
  const preguntar = debePreguntarReparto(gasto, historial);
  const categoria = visibleCategory(gasto.category);
  if (r === undefined && !preguntar && (!recordado || isMaterialCategory(categoria) || categoria === 'alquiler')) return null;
  const base = proponerReparto(gasto, hoy);
  const propuesta = recordado ? { ...base, reparto: recordado, explicacion: recordado.meses > 1 ? `La anterior la repartiste en ${recordado.meses} meses. Puedes repetirlo o cambiar el periodo.` : 'La anterior contó entera en su mes. Puedes repetirlo o repartir esta.', botonSi: recordado.meses > 1 ? `Sí, repartir en ${recordado.meses} meses` : 'Sí, entera en su mes' } : base;
  return (
    <div className="reparto reparto--pregunta">
      <p className="reparto__titulo">¿Este pago cubre varios meses?</p>
      {editando ? <EditarReparto reparto={propuesta.reparto} total={gasto.netAmount} historial={historial} hoy={hoy} onCancel={() => setEditando(false)} onChange={(nuevo) => { onChange({ ...value, reparto: decision(nuevo, hoy) }); setEditando(false); }} /> : <>
      <p>{propuesta.explicacion}</p>
      <div className="reparto__botones">
        <button type="button" className="btn btn--sm" onClick={() => onChange({ ...value, reparto: decision(propuesta.reparto, hoy) })}>{propuesta.botonSi}</button>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => onChange({ ...value, reparto: decision(repartoSoloDeEsteMes(gasto.date, hoy), hoy) })}>{propuesta.botonNo}</button>
      </div>
      <button type="button" className="reparto__enlace" onClick={() => setEditando(true)}>Otro número de meses</button>
      </>}
      <p className="reparto__gris">Si no contestas, cuenta entero en el mes del pago.</p>
    </div>
  );
}

function EditarReparto({ reparto, total, historial, hoy, onChange, onCancel }: { reparto: RepartoGasto; total: number; historial: ExpenseEntry[]; hoy: string; onChange: (r: RepartoGasto) => void; onCancel: () => void }): JSX.Element {
  const [meses, setMeses] = useState(String(Math.max(2, reparto.meses)));
  const [desde, setDesde] = useState(reparto.desde);
  const n = Number(meses);
  const valido = Number.isInteger(n) && n >= 2 && n <= MAX_MESES_REPARTO && /^\d{4}-(0[1-9]|1[0-2])$/.test(desde);
  const borrador: RepartoGasto = { ...reparto, meses: valido ? n : Math.max(2, reparto.meses), desde: /^\d{4}-(0[1-9]|1[0-2])$/.test(desde) ? desde : reparto.desde, partes: undefined };
  const bases = useBasesReparto(borrador, reparto.desde, historial);
  const como = reparto.como === 'iguales' ? 'en partes iguales' : reparto.como === 'ventas' ? 'según las ventas de cada mes' : 'según los sueldos de cada mes';
  const aviso = valido ? avisoMesesCerrados(borrador, hoy) : undefined;
  const mapa = reparto.como === 'ventas' ? bases.ventas : bases.sueldos;
  const mesesBase = mesesDelReparto(borrador);
  const faltanBases = reparto.como !== 'iguales' && (!mesesBase.every((m) => mapa.has(m)) || !mesesBase.some((m) => (mapa.get(m) ?? 0) > 0));
  return <div className="reparto__personalizar">
    <div className="reparto__editar">
      <label className="field">
        <span className="field__label">Repartir en</span>
        <span className="reparto__cantidad">
          <button type="button" className="btn btn--sm" aria-label="Un mes menos" disabled={n <= 2} onClick={() => setMeses(String(Math.max(2, (Number(meses) || 2) - 1)))}>−</button>
          <input className="input" aria-label="Número de meses" type="number" inputMode="numeric" min={2} max={MAX_MESES_REPARTO} step={1} value={meses} aria-invalid={!valido} onChange={(e) => setMeses(e.currentTarget.value)} />
          <button type="button" className="btn btn--sm" aria-label="Un mes más" disabled={n >= MAX_MESES_REPARTO} onClick={() => setMeses(String(Math.min(MAX_MESES_REPARTO, (Number(meses) || 2) + 1)))}>+</button>
          <span>meses</span>
        </span>
      </label>
      <label className="field"><span className="field__label">Desde</span><input className="input" type="month" value={desde} onChange={(e) => setDesde(e.currentTarget.value)} /></label>
    </div>
    {valido ? <>
      <p>Se reparte en {n} meses, {como}.</p>
      <p className="reparto__resumen">{resumenReparto(total, borrador, formatMoney, bases)}</p>
      {faltanBases && <p className="reparto__gris">Faltan {reparto.como === 'ventas' ? 'ventas' : 'sueldos'} para calcular el reparto: por ahora se divide en partes iguales. Se ajustará cuando los cargues.</p>}
      {aviso && <p className="reparto__cerrados">{aviso}</p>}
    </> : <p className="reparto__cerrados" role="alert">Elige de 2 a 24 meses y un mes de inicio válido.</p>}
    <div className="reparto__botones">
      <button type="button" className="btn btn--sm" disabled={!valido} onClick={() => onChange(borrador)}>Sí, repartir en {valido ? n : '…'} meses</button>
      <button type="button" className="reparto__enlace" onClick={onCancel}>Cancelar</button>
    </div>
  </div>;
}
